import { ExternalLink, Globe, Search, Layers } from 'lucide-react'
import { PATENT_NODE_TYPES } from '../../constants/nodeTypes'
import type { ConnectionsNode } from '../../types'

interface PatentNodeDetailPanelProps {
  node: ConnectionsNode
  onClose: () => void
  /** Navigate to a similar_patent/family node's own record (+ its own Connections). */
  onOpenPatent: (patentId: string) => void
  /** "See other patents by this inventor" — mirrors the existing Patents-list search mode. */
  onInventorSearch: (name: string) => void
  /** "See other patents by this assignee" — new #4612/#4646 search mode. */
  onAssigneeSearch: (name: string) => void
  /** "Browse this category" — routes to the existing CPC-section browse flow. */
  onBrowseCategory: (label: string) => void
}

/**
 * #4612/#4646: purpose-built detail panel for Patent Connections graph nodes.
 * NOT a reuse of the generic (invention-oriented) NodeDetailPanel, which
 * hardcodes disclosure/save-to-list/outreach/prior-art actions that are
 * either meaningless or would have to be faked for a patent node — this
 * panel only ever shows an action that is real and already wired.
 */
export default function PatentNodeDetailPanel({
  node, onClose, onOpenPatent, onInventorSearch, onAssigneeSearch, onBrowseCategory,
}: PatentNodeDetailPanelProps) {
  const typeConfig = PATENT_NODE_TYPES[node.type] ?? PATENT_NODE_TYPES.patent
  // Patent ids are always CC-digits[-kind] (verified by _PATENT_ID_RE) — the
  // leading 2 letters are the jurisdiction, no separate field needed.
  const jurisdiction = /^[A-Z]{2}/.test(node.id) ? node.id.slice(0, 2) : null

  return (
    <div className="light-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Node Detail</h3>
        <button onClick={onClose} className="p-0.5 text-gray-500 hover:text-gray-600">
          &times;
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: typeConfig.color }}>
            <div className="w-3 h-3 bg-white/30 rounded-sm" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{node.label}</p>
            <p className="text-xs text-gray-500">{typeConfig.label}</p>
          </div>
        </div>

        {(node.type === 'similar_patent' || node.type === 'family') && jurisdiction && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Globe size={11} /> {jurisdiction}
          </div>
        )}

        {node.score !== undefined && (
          <div>
            <span className="text-xs text-gray-500 block">Similarity</span>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-sky-200 rounded-full overflow-hidden">
                <div className="h-full bg-iflow-500 rounded-full" style={{ width: `${Math.round(node.score * 100)}%` }} />
              </div>
              <span className="text-xs text-gray-600">{Math.round(node.score * 100)}%</span>
            </div>
          </div>
        )}

        {(node.type === 'similar_patent' || node.type === 'family') && (
          <button
            onClick={() => onOpenPatent(node.id)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-iflow-600 border border-iflow-300 rounded-lg hover:bg-iflow-50 transition-colors"
          >
            <ExternalLink size={12} /> Open full record
          </button>
        )}

        {node.type === 'inventor' && (
          <button
            onClick={() => onInventorSearch(node.label)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-iflow-600 border border-iflow-300 rounded-lg hover:bg-iflow-50 transition-colors"
          >
            <Search size={12} /> See other patents by this inventor
          </button>
        )}

        {node.type === 'assignee' && (
          <button
            onClick={() => onAssigneeSearch(node.label)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-iflow-600 border border-iflow-300 rounded-lg hover:bg-iflow-50 transition-colors"
          >
            <Search size={12} /> See other patents by this assignee
          </button>
        )}

        {node.type === 'cpc' && (
          <button
            onClick={() => onBrowseCategory(node.label)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-iflow-600 border border-iflow-300 rounded-lg hover:bg-iflow-50 transition-colors"
          >
            <Layers size={12} /> Browse this category
          </button>
        )}

        {node.type === 'patent' && (
          <p className="text-xs text-gray-500">This is the patent you're viewing.</p>
        )}
      </div>
    </div>
  )
}
